import Header from "@components/Header/Header";
import PINInput from "@components/PINInput/PINInput";
import { useNavigate } from "react-router";
import { PageContainer, ContentContainer } from "./Authentication.styles";
import { INTER } from "@styles/fonts";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Biometrics } from "@lib/telegram";
import { useBiometricsStore } from "@stores/biometricsStore";

export type AuthenticationType = 'validate' | 'validate:unlock' | 'create' | 'change';

interface AuthenticationProps {
  /**
   * Title to display in the header
   */
  headerTitle?: string;
  /**
   * Title to display in the PIN input
   */
  title?: string;
  /**
   * Description to display in the PIN input
   */
  description?: string;
  /**
   * Custom back route
   */
  backRoute?: string;
  /**
   * Callback when authentication is successful
   * This is where each operation would implement its API call
   */
  onSuccess?: () => void;
  /**
   * Callback when authentication fails
   */
  onFailure?: () => void;
  /**
   * Route to navigate to after successful API call
   */
  successRoute?: string;
  /**
   * Use alternative style for PIN input
   */
  useAlternativeStyle?: boolean;
  /**
   * Enable biometrics
   */
  biometrics?: boolean;
}

const Authentication: React.FC<AuthenticationProps> = ({
  headerTitle,
  title,
  description,
  backRoute,
  onSuccess,
  onFailure,
  successRoute = '/home',
  useAlternativeStyle = false,
  biometrics = false
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { isEnabled } = useBiometricsStore()

    useEffect(() => {
        const onBiometricSuccess = () => {
            onSuccess?.();
        }

        const onBiometricFailure = () => {
            onFailure?.();
        }

        if (biometrics && isEnabled) {
            Biometrics.requestBiometricsAuth(onBiometricSuccess, onBiometricFailure)
        }
    }, [biometrics])
    
    // Handle back button click
    const handleBackClick = () => {
        if (backRoute) {
            navigate(backRoute);
        } else {
            navigate(-1);
        }
    };
    
    // Handle PIN completion
    const handlePinComplete = (result: { success: boolean; newPin?: string }) => {
        if (result.success) {
            if (onSuccess) {
                // Call the operation-specific success handler
                onSuccess();
            } else {
                // Default behavior: navigate to success route
                navigate(successRoute);
            }
        } else {
            if (onFailure) {
                onFailure();
            }
        }
    };

    const customStyle = useAlternativeStyle ? {
        titleFontSize: 18,
        titleFontFamily: INTER.semiBold,
        descriptionFontSize: 14,
        descriptionFontFamily: INTER.extraLight
    } : undefined
    
    return (
        <PageContainer>
            <Header 
                title={headerTitle || t('authentication.defaultHeaderTitle')} 
                showBackButton
                onBackClick={handleBackClick} 
            />
            <ContentContainer>
                <PINInput
                    mode={'validate'}
                    enableTitle={!!title}
                    enableDescription={!!description}
                    customDescription={description}
                    customTitle={title}
                    onComplete={handlePinComplete}
                    alternativeStyle={useAlternativeStyle}
                    {...customStyle}
                />
            </ContentContainer>
        </PageContainer>
    );
};

export default Authentication;